import { Check } from "lucide-react";
import {
  useDataProvider,
  useGetIdentity,
  useGetList,
  useNotify,
  useTranslate,
} from "ra-core";
import { useEffect, useState, type FormEvent } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import { useInvalidate } from "../misc/useInvalidate";
import { useConfigurationContext } from "../root/ConfigurationContext";
import type { Deal, WorkItem } from "../types";
import { formatISODateString } from "./dealUtils";
import { suggestNextStep } from "./nextStep";
import {
  NextStepFields,
  useTaskTypeLabel,
  validateNextStep,
  type NextStepDraft,
} from "./NextStepFields";
import { activeNextStepDialogs } from "./taskCompletionEvents";

// «Hva er neste steg?» (kap. 7.1 og 16.7). Viser de åpne stegene (de kan
// fullføres herfra) og lar oss planlegge et nytt med stadiets forslag. Enter
// godtar forslaget, Esc hopper over. Etter en fullført oppgave betyr «Hopp
// over» at muligheten står uten neste steg.
export const DealNextStepDialog = ({
  deal,
  onClose,
  afterComplete,
}: {
  deal: Deal | null;
  onClose: (planned: boolean) => void;
  afterComplete?: boolean;
}) => {
  const translate = useTranslate();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const invalidate = useInvalidate();
  const { identity } = useGetIdentity();
  const { dealStages } = useConfigurationContext();
  const typeLabel = useTaskTypeLabel();
  const stage = dealStages.find((s) => s.value === deal?.stage);
  const fallbackTitle = translate("crm.deals.next.default_title", {
    _: "Neste steg",
  });
  const [draft, setDraft] = useState<NextStepDraft>(() =>
    suggestNextStep(stage, { fallbackTitle }),
  );
  const [errors, setErrors] = useState<{ title?: string; due_date?: string }>(
    {},
  );
  const [saving, setSaving] = useState(false);

  const dealKey = deal ? String(deal.id) : null;
  useEffect(() => {
    if (!dealKey) return;
    setDraft(suggestNextStep(stage, { fallbackTitle }));
    setErrors({});
    activeNextStepDialogs.add(dealKey);
    return () => {
      activeNextStepDialogs.delete(dealKey);
    };
    // Forslaget lages når dialogen åpnes for en mulighet.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealKey]);

  const { data: openTasks = [], refetch } = useGetList<WorkItem>(
    "work_items",
    {
      filter: { deal_id: deal?.id, "completed_at@is": null },
      sort: { field: "due_date", order: "ASC" },
      pagination: { page: 1, perPage: 20 },
    },
    { enabled: deal != null },
  );

  const complete = async (task: WorkItem) => {
    await dataProvider.update<WorkItem>("work_items", {
      id: task.id,
      data: { completed_at: new Date().toISOString() },
      previousData: task,
    });
    notify(
      translate("crm.deals.next.completed", {
        _: "Fullført: %{title}",
        title: task.title,
      }),
    );
    await refetch();
    invalidate("work_items", "deals", "company_notes");
  };

  const submit = async (event?: FormEvent) => {
    event?.preventDefault();
    if (!deal) return;
    const found = validateNextStep(draft, translate);
    setErrors(found);
    if (Object.keys(found).length) return;
    setSaving(true);
    try {
      await dataProvider.create("work_items", {
        data: {
          title: draft.title.trim(),
          task_type: draft.type,
          due_date: draft.due_date,
          deal_id: deal.id,
          company_id: deal.company_id ?? null,
          assignee_id: deal.sales_id ?? identity?.id ?? null,
          sales_id: identity?.id ?? null,
        },
      });
      notify(
        translate("crm.deals.next.planned", { _: "Neste steg er planlagt" }),
      );
      invalidate("work_items", "deals");
      onClose(true);
    } catch (error) {
      notify((error as Error)?.message || "ra.notification.http_error", {
        type: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={deal != null}
      onOpenChange={(open) => !open && onClose(false)}
    >
      <DialogContent
        className="sm:max-w-xl"
        onKeyDown={(e) => {
          if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            submit();
          }
        }}
      >
        <form onSubmit={submit} className="flex flex-col gap-4">
          <DialogHeader>
            <DialogTitle>
              {translate("crm.deals.next.title", { _: "Hva er neste steg?" })}
            </DialogTitle>
            <DialogDescription>
              {translate("crm.deals.next.description", {
                _: "Hver mulighet trenger et neste steg. Hva gjør vi, og når?",
              })}
              {deal ? ` ${deal.name}` : ""}
            </DialogDescription>
          </DialogHeader>
          {openTasks.length > 0 && (
            <section className="flex flex-col gap-1.5">
              <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                {translate("crm.deals.next.open_steps", { _: "Åpne steg" })}
              </h3>
              <ul className="flex flex-col divide-y rounded-md border">
                {openTasks.map((task) => (
                  <li
                    key={task.id}
                    className="flex items-center gap-3 px-3 py-2 text-sm"
                  >
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="size-6 shrink-0"
                      aria-label={translate("crm.deals.next.complete", {
                        _: "Fullfør %{title}",
                        title: task.title,
                      })}
                      onClick={() => complete(task)}
                    >
                      <Check className="h-3.5 w-3.5" />
                    </Button>
                    <span className="min-w-0 flex-1 truncate">
                      {task.title}
                    </span>
                    <span className="shrink-0 text-xs text-muted-foreground">
                      {typeLabel(task.task_type)}
                      {task.due_date
                        ? ` · ${formatISODateString(task.due_date)}`
                        : ""}
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          )}
          <NextStepFields
            idPrefix="next-step"
            value={draft}
            onChange={setDraft}
            autoFocus
            errors={errors}
          />
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => onClose(false)}
              disabled={saving}
            >
              {afterComplete
                ? translate("crm.deals.next.skip", { _: "Hopp over" })
                : translate("ra.action.cancel", { _: "Avbryt" })}
            </Button>
            <Button type="submit" disabled={saving}>
              {translate("crm.deals.next.plan", { _: "Planlegg neste steg" })}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
